/**
 * SiteCombobox — searchable, alphabetical site selector.
 *
 * Props:
 *   sites        {Array}   — objects with at minimum { id, name }; optionally { client_name, currency }
 *   value        {string}  — currently selected site id (string)
 *   onValueChange {fn}     — called with the selected id string
 *   placeholder  {string}  — button placeholder text
 *   disabled     {bool}
 *   testId       {string}  — data-testid on the trigger button
 *   noneOption   {string|null} — if set, renders a "none" option with this label (value "__none__")
 *   showCurrency {bool}    — append (currency) to display label
 */
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';

export default function SiteCombobox({
  sites = [],
  value,
  onValueChange,
  placeholder = 'Choose a site…',
  disabled = false,
  testId,
  noneOption = null,
  showCurrency = false,
}) {
  const [open, setOpen] = useState(false);

  // Sort alphabetically: client_name then name, case-insensitive
  const sorted = [...sites].sort((a, b) => {
    const keyA = `${a.client_name || ''} ${a.name || ''}`.trim().toLowerCase();
    const keyB = `${b.client_name || ''} ${b.name || ''}`.trim().toLowerCase();
    return keyA.localeCompare(keyB);
  });

  const selected = sorted.find(s => String(s.id) === String(value));

  const displayLabel = selected
    ? (selected.client_name
        ? `${selected.client_name} / ${selected.name}`
        : selected.name)
    + (showCurrency && selected.currency ? ` (${selected.currency})` : '')
    : null;

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal h-9 text-left"
          data-testid={testId}
          type="button"
        >
          <span className="truncate">
            {displayLabel || <span className="text-slate-400">{placeholder}</span>}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by client or site name…" />
          <CommandList>
            <CommandEmpty>No sites found.</CommandEmpty>
            <CommandGroup>
              {noneOption && (
                <CommandItem
                  value="__none__"
                  onSelect={() => { onValueChange('__none__'); setOpen(false); }}
                  className="cursor-pointer text-slate-400 italic"
                >
                  {noneOption}
                </CommandItem>
              )}
              {sorted.map(s => (
                <CommandItem
                  key={s.id}
                  value={`${s.client_name || ''} ${s.name}`}
                  onSelect={() => { onValueChange(String(s.id)); setOpen(false); }}
                  className="cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    {s.client_name ? (
                      <span className="text-sm text-slate-700 truncate">
                        <span className="text-slate-400">{s.client_name}</span>
                        <span className="text-slate-300 mx-1">/</span>
                        {s.name}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-800 truncate">{s.name}</span>
                    )}
                  </div>
                  {showCurrency && s.currency && (
                    <span className="ml-auto text-xs text-slate-400 shrink-0 pl-2">{s.currency}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
